import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ESSAIS_UI, pickLang } from "@/lib/essaisText";
import { EssaiHeader } from "@/components/EssaiHeader";
import { Footer } from "@/components/Footer";

export const EssaiCard = ({ e, U, i = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7, delay: (i % 6) * 0.06 }}>
    <Link to={`/essais/${e.slug}`} className="group block h-full rounded-2xl overflow-hidden border border-primary/15 bg-card/40 hover:border-primary/50 transition-colors duration-500" data-testid={`essai-card-${e.slug}`}>
      <div className="relative aspect-square overflow-hidden">
        <img src={`/derive/covers/${e.slug}.jpg`} alt="" loading="lazy" className="h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      </div>
      <div className="p-5">
        <span className="text-[0.65rem] uppercase tracking-[0.22em] text-primary">{U.essay} N°{e.num}</span>
        <h3 className="font-display text-xl leading-snug group-hover:text-primary transition-colors">{e.title}</h3>
        <p className="mt-2 text-sm text-foreground/60 line-clamp-3">{e.subtitle}</p>
      </div>
    </Link>
  </motion.div>
);

export default function Essais() {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const U = ESSAIS_UI[lang];
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/essais", { params: { lang } }).then(({ data }) => setList(data)).catch(() => setList([])); }, [lang]);
  const shown = useMemo(() => (list || []).filter((e) => `${e.title} ${e.subtitle}`.toLowerCase().includes(q.toLowerCase())), [list, q]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <EssaiHeader to="/" label="Cocoon" />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 pt-32 pb-24" data-testid="essais-page">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{U.kicker}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl" data-testid="essais-title">{U.series}</h1>
        <p className="mt-6 max-w-2xl text-foreground/70 text-base sm:text-lg">{U.lead}</p>
        <div className="mt-10 relative max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
          <input value={q} onChange={(ev) => setQ(ev.target.value)} placeholder={U.search} data-testid="essais-search-input"
            className="w-full rounded-full bg-card/50 border border-primary/20 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-primary/60" />
        </div>
        {!list && <Loader2 className="mt-16 animate-spin text-primary" data-testid="essais-loading" />}
        {list && !list.length && <p className="mt-16 text-foreground/60" data-testid="essais-empty">{U.empty}</p>}
        {list?.length > 0 && !shown.length && <p className="mt-12 text-foreground/60" data-testid="essais-no-results">{U.noResults}</p>}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="essais-grid">
          {shown.map((e, i) => <EssaiCard key={e.slug} e={e} U={U} i={i} />)}
        </div>
      </main>
      <Footer />
    </div>
  );
}
